import { Component } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { paloIsPlaceholderPrice } from '@synergy/components-palo';

import { OfferItem } from '../../../../../common/models/offer-item';

const styles = (theme: Theme) =>
    createStyles({
        outerContainer: {
            padding: theme.spacing(1),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
            height: '100%',
            width: '100%',
            display: 'inline-table',
        },
        container: {
            display: 'flex',
            justifyContent: 'space-between',
            '& div:last-child': {
                textAlign: 'right',
            },
        },
        divider: {
            borderLeft: `1px solid ${theme.palette.divider}`,
        },
        defaultText: {
            fontSize: '13px',
            lineHeight: '16px',
            color: '#21272E',
            fontWeight: 600,
            display: 'block',
        },
        lightText: {
            fontSize: '12px',
            lineHeight: '14px',
            color: '#21272E;',
            opacity: 0.6,
            display: 'block',
        },
        notOffered: {},
        alternative: {
            paddingBottom: theme.spacing(),
        },
        bestOffer: {
            marginTop: 3,
            fontSize: '11px',
            lineHeight: '13px',
            color: theme.palette.primary.main,
            textTransform: 'uppercase',
            '&:empty:before': {
                content: '"\\200b"',
            },
        },
    });

interface PriceMatrixPriceProps extends WithStyles<typeof styles> {
    currency: string;
    showDividers?: boolean;
    offerItem: OfferItem;
    bestPrice: boolean;
}

export const PriceMatrixPrice = withStyles(styles)(
    class PriceMatrixPriceBase extends Component<PriceMatrixPriceProps> {
        public render() {
            const { classes, offerItem, bestPrice } = this.props;
            const price = offerItem.price || 0;

            // A round 999.999 is what a supplier types to get past a required
            // field, not a price. Printed as money it reads as one (LST-S12).
            if (paloIsPlaceholderPrice(price, true)) {
                return (
                    <div className={`${classes.outerContainer} ${this.props.showDividers && classes.divider}`}>
                        <div className={classes.container}>
                            <span className={classes.lightText}>
                                <FormattedMessage id="app.price.onRequest" />
                            </span>
                        </div>
                    </div>
                );
            }

            if (offerItem.isOffered || offerItem.isAlternative) {
                return (
                    <div className={`${classes.outerContainer} ${this.props.showDividers && classes.divider}`}>
                        {offerItem.isAlternative && (
                            <div className={classes.alternative}>
                                <span className={classes.defaultText}>
                                    <FormattedMessage id="app.inquiries.detail.isAlternative" />
                                    <br />
                                </span>
                                {offerItem.alternativeNote}
                            </div>
                        )}
                        <div className={classes.container}>
                            <div>
                                <span className={classes.defaultText}>
                                    <FormattedMessage id="app.priceMatrix.positionPrice" />
                                </span>
                                <span className={classes.lightText}>
                                    <FormattedMessage
                                        id="app.priceMatrix.perItemPrice"
                                        values={{ value: offerItem.orderUnit }}
                                    />
                                </span>
                                {offerItem.contentQuantity && (
                                    <span className={classes.lightText}>
                                        <FormattedMessage
                                            id="app.priceMatrix.perItemPrice"
                                            values={{ value: offerItem.contentUnit }}
                                        />
                                    </span>
                                )}
                            </div>
                            <div>
                                <span className={classes.defaultText}>
                                    <FormattedNumber
                                        style="currency"
                                        currency={this.props.currency}
                                        value={price * offerItem.quantity}
                                    />
                                </span>
                                <span className={classes.lightText}>
                                    <FormattedNumber style="currency" currency={this.props.currency} value={price} />
                                </span>
                                {offerItem.contentQuantity && (
                                    <span className={classes.lightText}>
                                        <FormattedNumber
                                            style="currency"
                                            currency={this.props.currency}
                                            value={offerItem.contentQuantityPrice || 0}
                                        />
                                    </span>
                                )}
                            </div>
                        </div>
                        <span className={classes.bestOffer}>
                            {bestPrice && <FormattedMessage id="app.priceMatrix.bestPrice" />}
                        </span>
                    </div>
                );
            } else {
                return (
                    <div
                        className={`${classes.outerContainer} ${this.props.showDividers && classes.divider} ${
                            classes.notOffered
                        }`}
                    >
                        <FormattedMessage id="app.inquiries.detail.notOffered" />
                        <br />
                    </div>
                );
            }
        }
    }
);
